import { api } from "@/modules/api";
import styled from "@emotion/styled";
import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import { FC, useState } from "react";

export const SoftDeleteAccountDialog: FC<{
  accountId: string;
  profileDisplayName?: string | null;
  profileEmailAddress?: string | null;
  onSoftDeleted?: () => Promise<void> | void;
}> = ({ accountId, profileDisplayName, profileEmailAddress, onSoftDeleted }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const confirmTextIsValid = confirmText.trim().toUpperCase() === "CONFIRM";

  const open = () => {
    setConfirmText("");
    setConfirmError(null);
    setDeleteError(null);
    setIsOpen(true);
  };

  const close = () => {
    if (isDeleting) return;
    setIsOpen(false);
  };

  const handleSoftDelete = async () => {
    setConfirmError(null);
    setDeleteError(null);

    if (!confirmTextIsValid) {
      setConfirmError("Type CONFIRM to proceed.");
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.delete("/internal/v1/accounts/{account_id}", {
        params: { path: { account_id: accountId } },
      });

      if (response.error || !response.response?.ok) {
        setDeleteError(buildDeleteErrorMessage(response));
        return;
      }

      await onSoftDeleted?.();
      setIsOpen(false);
    } catch (error) {
      const fallbackMessage =
        error instanceof Error ? error.message : "Failed to delete account. Please try again.";
      setDeleteError(fallbackMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Button variant="outline" color="red" onClick={open} disabled={isDeleting}>
        {isDeleting ? "Deleting…" : "Soft Delete Account"}
      </Button>

      <Dialog.Root open={isOpen} onOpenChange={close}>
        <Dialog.Content style={{ maxWidth: "640px" }}>
          <Dialog.Title>Soft delete account</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            This action is intended for internal use and will be recorded for auditing.
          </Dialog.Description>

          <WarningBox>
            <Text weight="bold">Warning</Text>
            <Text size="2" color="gray">
              This will completely disable this account. If the user signs up again with the same
              email, it will be treated as a brand new account.
            </Text>
            <Text size="2" color="gray">
              De-provisioning can take a minute to complete.
            </Text>
            <br />
            <Text size="2" color="gray">
              Profile name: <b>{profileDisplayName ?? "—"}</b>
            </Text>
            <Text size="2" color="gray">
              Profile email: <b>{profileEmailAddress ?? "—"}</b>
            </Text>
            <Text size="2" color="gray">
              Account: <b>{accountId}</b>
            </Text>
          </WarningBox>

          <SectionHeading size="2" mb="2">
            Confirmation
          </SectionHeading>
          <Text size="2" color="gray">
            Type <b>CONFIRM</b> to proceed.
          </Text>
          <TextField.Root
            value={confirmText}
            onChange={event => {
              setConfirmText(event.currentTarget.value);
              if (confirmError) setConfirmError(null);
            }}
            onKeyDown={event => {
              if (event.key !== "Enter") return;
              event.preventDefault();

              if (isDeleting) return;
              if (!confirmTextIsValid) return;

              void handleSoftDelete();
            }}
            placeholder="Type CONFIRM to acknowledge the warning"
            mt="2"
          />
          {confirmError ? <ErrorText size="2">{confirmError}</ErrorText> : null}
          {deleteError ? <ErrorText size="2">{deleteError}</ErrorText> : null}

          <DialogActions>
            <Flex gap="2" justify="end">
              <Button variant="outline" onClick={close} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                onClick={handleSoftDelete}
                disabled={isDeleting || !confirmTextIsValid}
                color="red"
              >
                {isDeleting ? "Deleting…" : "Soft delete"}
              </Button>
            </Flex>
          </DialogActions>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

type DeleteApiResponse = Awaited<ReturnType<(typeof api)["delete"]>>;

const buildDeleteErrorMessage = (response: DeleteApiResponse) => {
  const status = response.response?.status;
  const statusText = status ? ` (status ${status})` : "";

  if (response.error && typeof response.error === "object") {
    const errorObject = response.error as {
      detail?: unknown;
      message?: unknown;
      error?: unknown;
    };

    if (typeof errorObject.detail === "string" && errorObject.detail.trim()) {
      return `Failed to delete account: ${errorObject.detail}${statusText}`;
    }

    if (typeof errorObject.message === "string" && errorObject.message.trim()) {
      return `Failed to delete account: ${errorObject.message}${statusText}`;
    }

    if (typeof errorObject.error === "string" && errorObject.error.trim()) {
      return `Failed to delete account: ${errorObject.error}${statusText}`;
    }
  }

  if (status && status >= 400) {
    return `Failed to delete account${statusText}.`;
  }

  return "Failed to delete account. Please try again.";
};

const SectionHeading = styled(Text)`
  display: block;
  font-weight: 600;
`;

const DialogActions = styled.div`
  margin-top: 20px;
`;

const WarningBox = styled.div`
  border: 1px solid var(--gray-6);
  background: var(--gray-2);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorText = styled(Text)`
  color: var(--red-11);
  display: block;
  margin-top: 6px;
`;
